import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  memo,
  useMemo,
} from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { toast } from "react-toastify";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import PdfInput from "../../components/CustomPdf/Inputs";
import {
  PdfDropdown,
  PdfRadio,
  PdfTextMultiline,
  PdfDate,
} from "../../components/CustomPdf/Inputs";
import PdfCheckBox from "../../components/CustomPdf/Inputs/checkbox";
import PdfInitials from "../../components/CustomPdf/Inputs/Initials";
import PdfSignature from "../../components/CustomPdf/Inputs/Signature";
import { checkMarkBase64 } from "./Inputs/checkMarkBase64";
import { postUploadData } from "../../api";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/Loader";
import BackButtonComponent from "../backButton";
import CancelIcon from "../../assets/images/custom_documentation/Cancel.svg";
import Preview from "../../assets/images/custom_documentation/Preview.svg";
import SaveIcon from "../../assets/images/custom_documentation/save.svg";
import Arrow from "../../assets/images/custom_documentation/Arrow.svg";
import ZoomOut from "../../assets/images/custom_documentation/Zoom Out.svg";
import ZoomIn from "../../assets/images/custom_documentation/Zoom In.svg";
import "./style.scss";
import "../../pages/CustomClientDocument/styles.scss";
import ReqIcon from "../../assets/images/custom_documentation/req_icon.svg";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const PdfField = memo(
  ({
    pageNumber,
    inputId,
    pageInputData,
    activeFieldId,
    handleChange,
    handleFieldOpen,
    handleFieldClose,
    enableErrorMsg,
    personalInfo,
    onboardingTasks,
    pdfFullData,
    zoom,
    validationErrors,
  }) => {
    const { type, top, left, width, height, value } = pageInputData;
    const fieldId = `${pageNumber}_${inputId}`;
    const hasValidationError = validationErrors[fieldId] && pageInputData.needed;

    const getFieldContent = () => {
      switch (type) {
        case "text":
          return (
            <div
              style={{
                position: "absolute",
                top: `${(top + 3) * zoom}px`,
                left: `${left * zoom}px`,
                width: `${width * zoom}px`,
                height: `${height * zoom}px`,
                zIndex:
                  activeFieldId && activeFieldId === `text_${inputId}` ? 3 : 2,
              }}
            >
              <PdfInput
                page={pageNumber}
                inputId={inputId}
                value={value || ""}
                place_holder={pageInputData.place_holder || ""}
                onChange={handleChange}
              />
              {hasValidationError && enableErrorMsg && (
                <p className="mb-0 err-feedback">
                  <img src={ReqIcon} alt="required-icon" />
                  This field is mandatory
                </p>
              )}
            </div>
          );

        case "checkbox":
          return (
            <div
              style={{
                position: "absolute",
                top: `${top * zoom}px`,
                left: `${left * zoom}px`,
                width: `${width * zoom}px`,
                height: `${height * zoom}px`,
                zIndex:
                  activeFieldId && activeFieldId === `checkbox_${inputId}`
                    ? 3
                    : 2,
              }}
            >
              <PdfCheckBox
                page={pageNumber}
                inputId={inputId}
                value={false}
                onChange={handleChange}
              />
              {hasValidationError && enableErrorMsg && (
                <p className="mb-0 err-feedback absolute-error">
                  <img src={ReqIcon} alt="required-icon" />
                  This field is mandatory
                </p>
              )}
            </div>
          );

        case "initials":
          return (
            <div
              style={{
                position: "absolute",
                top: `${top * zoom}px`,
                left: `${left * zoom}px`,
                width: `${width * zoom}px`,
                height: `${height * zoom}px`,
                zIndex:
                  activeFieldId && activeFieldId === `initials_${inputId}`
                    ? 3
                    : 2,
              }}
            >
              <PdfInitials
                page={pageNumber}
                inputId={inputId}
                value={value || ""}
                place_holder={pageInputData.place_holder || ""}
                onChange={handleChange}
              />
              {hasValidationError && enableErrorMsg && (
                <p className="mb-0 err-feedback absolute-error">
                  <img src={ReqIcon} alt="required-icon" />
                  This field is mandatory
                </p>
              )}
            </div>
          );

        case "signature":
          // Parse signerType from value
          let signerType = "";
          try {
            const signatureData = typeof value === "string" && value.startsWith("{")
              ? JSON.parse(value)
              : value;
            signerType = signatureData?.signerType || "";
          } catch (e) {
            // If parsing fails, continue with empty signerType
          }

          return (
            <div
              style={{
                position: "absolute",
                top: `${top * zoom}px`,
                left: `${left * zoom}px`,
                width: `${width * zoom}px`,
                height: `${height * zoom}px`,
                zIndex:
                  activeFieldId && activeFieldId === `signature_${inputId}`
                    ? 3
                    : 2,
              }}
            >
              <PdfSignature
                page={pageNumber}
                inputId={inputId}
                value={
                  value ||
                  onboardingTasks?.e_sign ||
                  pdfFullData?.e_sign ||
                  "Signature"
                }
                onChange={handleChange}
                e_sign={
                  value !== "[Signature]"
                    ? value
                    : onboardingTasks?.e_sign || pdfFullData?.e_sign
                }
                signerType={signerType}
              />
              {hasValidationError && enableErrorMsg && (
                <p className="mb-0 err-feedback">
                  <img src={ReqIcon} alt="required-icon" />
                  This field is mandatory
                </p>
              )}
            </div>
          );

        case "textarea":
          return (
            <div
              style={{
                position: "absolute",
                top: `${top * zoom}px`,
                left: `${left * zoom}px`,
                width: `${width * zoom}px`,
                height: `${height * zoom}px`,
                zIndex:
                  activeFieldId && activeFieldId === `textarea_${inputId}`
                    ? 3
                    : 2,
              }}
            >
              <PdfTextMultiline
                page={pageNumber}
                inputId={inputId}
                value={value || ""}
                place_holder={pageInputData.place_holder || ""}
                onChange={handleChange}
              />
              {hasValidationError && enableErrorMsg && (
                <p className="mb-0 err-feedback">
                  <img src={ReqIcon} alt="required-icon" />
                  This field is mandatory
                </p>
              )}
            </div>
          );

        case "select":
          return (
            <div
              style={{
                position: "absolute",
                top: `${top * zoom}px`,
                left: `${left * zoom}px`,
                width: `${width * zoom}px`,
                height: `${height * zoom}px`,
                zIndex:
                  activeFieldId && activeFieldId === `select_${inputId}`
                    ? 3
                    : 2,
              }}
            >
              <PdfDropdown
                page={pageNumber}
                inputId={inputId}
                value={pageInputData.multiple_options ? (value || []) : (value || "")}
                place_holder={pageInputData.place_holder || "Select an option"}
                onChange={handleChange}
                onOpen={() => handleFieldOpen(`select_${inputId}`)}
                onClose={() => handleFieldClose(`select_${inputId}`)}
                options={pageInputData.options || []}
                multiple_options={pageInputData.multiple_options || false}
              />
              {hasValidationError && enableErrorMsg && (
                <p className="mb-0 err-feedback">
                  <img src={ReqIcon} alt="required-icon" />
                  This field is mandatory
                </p>
              )}
            </div>
          );

        case "radio":
          return (
            <div
              style={{
                position: "absolute",
                top: `${top * zoom}px`,
                left: `${left * zoom}px`,
                width: `${width * zoom}px`,
                height: `${height * zoom}px`,
                zIndex:
                  activeFieldId && activeFieldId === `radio_${inputId}` ? 3 : 2,
              }}
            >
              <PdfRadio
                page={pageNumber}
                inputId={inputId}
                value={false}
                place_holder={pageInputData.place_holder || ""}
                onChange={handleChange}
                options={pageInputData.options || []}
                name={pageInputData.radio_group ? `radio-group-${pageInputData.radio_group}` : `radio-page-${pageNumber}`}
              />
              {hasValidationError && enableErrorMsg && (
                <p className="mb-0 err-feedback">
                  <img src={ReqIcon} alt="required-icon" />
                  This field is mandatory
                </p>
              )}
            </div>
          );

        case "date":
          return (
            <div
              style={{
                position: "absolute",
                top: `${top * zoom}px`,
                left: `${left * zoom}px`,
                width: `${width * zoom}px`,
                height: `${height * zoom}px`,
                zIndex:
                  activeFieldId && activeFieldId === `date_${inputId}` ? 3 : 2,
              }}
            >
              <PdfDate
                page={pageNumber}
                inputId={inputId}
                value={null}
                place_holder={pageInputData.place_holder || ""}
                onChange={handleChange}
                onOpen={() => handleFieldOpen(`date_${inputId}`)}
                onClose={() => handleFieldClose(`date_${inputId}`)}
                isActive={activeFieldId === `date_${inputId}`}
              />
              {hasValidationError && enableErrorMsg && (
                <p className="mb-0 err-feedback">
                  <img src={ReqIcon} alt="required-icon" />
                  This field is mandatory
                </p>
              )}
            </div>
          );

        default:
          return null;
      }
    };

    return getFieldContent();
  },
);
const FieldsRenderer = memo(
  ({
    selectPage,
    activeFieldId,
    handleChange,
    handleFieldOpen,
    handleFieldClose,
    enableErrorMsg,
    personalInfo,
    onboardingTasks,
    pdfFullData,
    settingsData,
    validationErrors,
    zoom,
  }) => {
    return Object.entries(settingsData.current).map(
      ([pageNumber, pageData]) => {
        if (pageNumber !== selectPage.toString()) return null;

        return Object.entries(pageData).map(([inputId, pageInputData]) => (
          <PdfField
            key={`${pageInputData.type}_${inputId}`}
            pageNumber={pageNumber}
            inputId={inputId}
            pageInputData={pageInputData}
            activeFieldId={activeFieldId}
            handleChange={handleChange}
            handleFieldOpen={handleFieldOpen}
            handleFieldClose={handleFieldClose}
            enableErrorMsg={enableErrorMsg}
            personalInfo={personalInfo}
            onboardingTasks={onboardingTasks}
            pdfFullData={pdfFullData}
            zoom={zoom}
            validationErrors={validationErrors}
          />
        ));
      },
    );
  },
);
const ZoomControls = memo(({ zoom, onZoomIn, onZoomOut }) => {
  return (
    <div className="zoom-number">
      <p
        className={`zoom zoom-in menu-item${zoom >= 1.6 ? " disabled" : ""}`}
        onClick={onZoomIn}
      >
        <img src={ZoomIn} alt="zoom-in" />
        <span className="menu-label">Zoom In</span>
      </p>
      <p className="zoom-percent">{Math.round(zoom * 100)}%</p>
      <p
        className={`zoom zoom-out menu-item${zoom <= 0.6 ? " disabled" : ""}`}
        onClick={onZoomOut}
      >
        <img src={ZoomOut} alt="zoom-out" />
        <span className="menu-label">Zoom Out</span>
      </p>
    </div>
  );
});

const PdfViewer = ({
  file,
  pdfData,
  pdfFullData,
  pageType,
  personalInfo,
  onboardingTasks,
}) => {
  let navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const canvasData = useMemo(
    () => JSON.parse(pdfData?.canvas_settings),
    [pdfData?.canvas_settings],
  );
  const settingsData = useRef(JSON.parse(pdfData?.settings));

  // Clear all field values (except signatures) to ensure proper validation and user interaction (TaskViewPage approach)
  useEffect(() => {
    if (settingsData.current) {
      Object.keys(settingsData.current).forEach(page => {
        Object.keys(settingsData.current[page]).forEach(fieldId => {
          const field = settingsData.current[page][fieldId];
          if (field.type === 'signature') {
            // Preserve signature values
            return;
          } else if (field.type === 'date') {
            field.value = null;
          } else if (field.type === 'checkbox' || field.type === 'radio') {
            field.value = false;
          } else {
            // Clear text, textarea, initials, and other field types to empty string
            field.value = "";
          }
        });
      });
    }
  }, []);

  const [selectPage, setSelectPage] = useState(1);
  const [numPages, setNumPages] = useState();
  const [zoom, setZoom] = useState(1);

  const containerStyle = useMemo(
    () => ({
      position: "relative",
      width: `${canvasData?.width}px`,
      height: `${canvasData?.height}px`,
    }),
    [canvasData?.width, canvasData?.height],
  );
  const [enableErrorMsg, setEnableErrorMsg] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isHeaderTouched, setIsHeaderTouched] = useState(false);
  const headerRef = useRef(null);

  const handleFieldOpen = useCallback((fieldId) => {
    setActiveFieldId(fieldId);
  }, []);

  const handleFieldClose = useCallback(
    (fieldId) => {
      if (activeFieldId === fieldId) {
        setActiveFieldId(null);
      }
    },
    [activeFieldId],
  );

  const handleZoomIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setZoom((currentZoom) => {
      if (currentZoom < 1.6) {
        const newZoom = Math.min(1.6, currentZoom + 0.1);
        return Math.round(newZoom * 100) / 100;
      }
      return currentZoom;
    });
  }, []);

  const handleZoomOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setZoom((currentZoom) => {
      if (currentZoom > 0.6) {
        const newZoom = Math.max(0.6, currentZoom - 0.1);
        return Math.round(newZoom * 100) / 100;
      }
      return currentZoom;
    });
  }, []);

  const handlePreviousPage = useCallback(() => {
    setSelectPage((currentPage) => {
      if (currentPage > 1) {
        return currentPage - 1;
      }
      return currentPage;
    });
  }, []);

  const handleNextPage = useCallback(() => {
    setSelectPage((currentPage) => {
      if (currentPage < numPages) {
        return currentPage + 1;
      }
      return currentPage;
    });
  }, [numPages]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "+" || e.key === "-" || e.key === "0" || e.key === "=")
      ) {
        e.preventDefault();
        e.stopPropagation();

        if (e.key === "+" || e.key === "=") {
          setZoom((currentZoom) => {
            const newZoom = Math.min(1.6, currentZoom + 0.1);
            return newZoom;
          });
        } else if (e.key === "-") {
          setZoom((currentZoom) => {
            const newZoom = Math.max(0.6, currentZoom - 0.1);
            return newZoom;
          });
        } else if (e.key === "0") {
          setZoom(1.0); // Reset to 100%
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleChange = useCallback((page, inputId, newValue) => {
    const currentInput = settingsData.current[page][inputId];

    if (
      currentInput &&
      currentInput.characterLimit &&
      typeof newValue === "string"
    ) {
      if (
        currentInput.type === "text" ||
        currentInput.type === "textarea" ||
        currentInput.type === "initials"
      ) {
        newValue = newValue.substring(0, currentInput.characterLimit);
      }
    }

    if (currentInput && currentInput.type === "radio") {
      // Get the radio group name for the current field
      const radioGroupName = currentInput.radio_group ? `radio-group-${currentInput.radio_group}` : `radio-page-${page}`;
      
      if (newValue === true) {
        // When selecting a radio button, clear other radio buttons in the same group
        Object.keys(settingsData.current[page]).forEach((key) => {
          const otherInput = settingsData.current[page][key];
          if (key !== inputId && otherInput && otherInput.type === "radio") {
            // Only clear radio buttons in the same group
            const otherRadioGroupName = otherInput.radio_group ? `radio-group-${otherInput.radio_group}` : `radio-page-${page}`;
            if (otherRadioGroupName === radioGroupName) {
              otherInput.value = false;
              otherInput.place_holder = false;
            }
          }
        });
      }
      // Note: When newValue === false (deselection), we don't need to do anything
      // The browser radio button behavior will handle the deselection automatically
    }

    // Update the field value
    settingsData.current[page][inputId].value = newValue;

    // Clear validation error for this field if it had one (TaskViewPage approach)
    if (validationErrors[`${page}_${inputId}`]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${page}_${inputId}`];
        return newErrors;
      });
    }
  }, [validationErrors]);

  const onPdfLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    // Add a small delay to ensure smooth transition
    setTimeout(() => {
      setIsPdfLoading(false);
    }, 100);
  }, []);

  const onDocumentLoadError = useCallback((error) => {
    console.error("Error loading PDF:", error);
    setIsPdfLoading(false);
  }, []);

  const handleCancelBtnClick = () => {
    if (pageType === "employee-onboard") {
      navigate("/employee-onboard-sections#onboarding_tasks", {
        state: pdfFullData,
      });
    } else if (pageType === "profile_task") {
      const state = { state: "tasks_from_pdf", user_id: pdfFullData.user_id };
      navigate("/profile/" + pdfFullData.user_id, { state: state });
    }
  };

  const isFieldEmpty = (field) => {
    const { type, value } = field;

    switch (type) {
      case "text":
      case "textarea":
      case "date":
      case "initials":
        return value === "" || value === null || value === undefined;

      case "select":
      case "Dropdown":
        // Handle multiple options dropdown validation
        if (field.multiple_options) {
          if (!value || !Array.isArray(value) || value.length === 0) {
            return true;
          }
          // Check if all selected values are placeholders
          const placeholderText = (Array.isArray(field.place_holder) ? 'Select an option' : field.place_holder) || 'Select an option';
          return value.length === 1 && 
                 (value[0] === 'Select an option' || 
                  value[0] === placeholderText || 
                  value[0] === '');
        }
        return value === null || value === "" || value === undefined;

      case "checkbox":
      case "radio":
        return (
          value === "" ||
          value === null ||
          value === undefined ||
          value === false
        );

      case "signature":
        // Check if signature is empty, null, undefined, placeholder, or doesn't contain valid image data
        if (!value || value === "" || value === null || value === undefined || value === "[Signature]") {
          return true;
        }
        
        // Extract signature image data from various possible formats
        let signatureImageData = null;
        
        try {
          // Handle JSON string format
          if (typeof value === "string" && value.startsWith("{")) {
            const signatureData = JSON.parse(value);
            signatureImageData = signatureData.signature || signatureData.signatureData || signatureData.image || signatureData.data;
          } 
          // Handle direct base64 image data
          else if (typeof value === "string" && value.startsWith("data:image/")) {
            signatureImageData = value;
          }
          // Handle object format
          else if (typeof value === "object" && value !== null) {
            signatureImageData = value.signature || value.signatureData || value.image || value.data;
          }
        } catch (e) {
          // If parsing fails, consider it empty
          return true;
        }
        
        // Return true if no valid image data found
        return !signatureImageData || !signatureImageData.startsWith("data:image/");

      default:
        return value === "" || value === null || value === undefined;
    }
  };

  const handleSaveBtnClick = async () => {
    // Check if PDF is still loading
    if (isPdfLoading) {
      toast.error(
        "PDF is not loaded yet. Please wait for the PDF to load completely.",
        {
          theme: "colored",
          autoClose: 1000,
        },
      );
      return;
    }

    // TaskViewPage validation approach: Check for empty required fields
    const requiredFields = [];
    for (const key in settingsData.current) {
      const item = settingsData.current[key];
      for (const subKey in item) {
        const subItem = item[subKey];
        if (subItem.needed) {
          requiredFields.push({
            id: `${key}_${subKey}`,
            page: key,
            fieldKey: subKey,
            ...subItem
          });
        }
      }
    }

    const emptyRequiredFields = requiredFields.filter(field => 
      isFieldEmpty(field)
    );
    
    // Set validation errors for empty required fields (TaskViewPage approach)
    const errors = {};
    emptyRequiredFields.forEach(field => {
      errors[field.id] = true;
    });
    setValidationErrors(errors);
    
    if (emptyRequiredFields.length > 0) {
      setEnableErrorMsg(true);
      toast.error("Please ensure all mandatory fields are filled out.", {
        theme: "colored",
        autoClose: 1000,
      });
    } else {
      // Clear validation errors if all fields are valid
      setValidationErrors({});
      // api call
      await savePdf();
    }
  };

  const DownloadPdf = async () => {
    try {
      if (!file) {
        toast.error("No PDF file available", { theme: "colored", autoClose: 3000 });
        return;
      }
      
      const response = await fetch(file);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
      }
      
      const existingPdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      
      for (const pageKey in settingsData.current) {
        const pageData = settingsData.current[pageKey];
        const pageIndex = parseInt(pageKey) - 1;
        // Skip if page doesn't exist in the PDF
        if (pageIndex < 0 || pageIndex >= pdfDoc.getPageCount()) {
          console.warn(`Skipping page ${pageKey} - PDF only has ${pdfDoc.getPageCount()} pages`);
          continue;
        }
        const page = pdfDoc.getPage(pageIndex);
        
        // Calculate scale factor between canvas dimensions and actual PDF page dimensions
        const pdfPageWidth = page.getWidth();
        const pdfPageHeight = page.getHeight();
        const canvasWidth = (canvasData?.width && canvasData.width > 0) ? canvasData.width : pdfPageWidth;
        const canvasHeight = (canvasData?.height && canvasData.height > 0) ? canvasData.height : pdfPageHeight;
        const scaleX = canvasWidth > 0 ? pdfPageWidth / canvasWidth : 1;
        const scaleY = canvasHeight > 0 ? pdfPageHeight / canvasHeight : 1;
        
        for (const fieldKey in pageData) {
          const fieldData = pageData[fieldKey];
          const type = fieldData.type;
          const top = (fieldData.top || fieldData.y || 0) * scaleY;
          const left = (fieldData.left || fieldData.x || 0) * scaleX;
          const width = (fieldData.width || 100) * scaleX;
          const height = (fieldData.height || 30) * scaleY;
          const value = fieldData.value || "";
          const fontSize = parseFloat(fieldData.font_size || fieldData.fontSize) || 12;
          const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
          const zIndex = 99;

          let positionX, positionY;
          if (type === "checkbox" || type === "radio") {
            const elementSize = (type === "checkbox") ? 10 : 7; 
            positionX = left + (width / 2) - (elementSize / 2); 
            positionY = page.getHeight() - top - (height / 2) - (elementSize / 2); 
            if (type === "radio") {
              positionY += 3.75;
              positionX += 7.5; // Move radio fields 10px to the right
            }
          } else if (type === "textarea") {
            positionX = left + 3.75; 
            positionY = Math.max(0, page.getHeight() - top);
          } else {
            positionX = left + 3.75; 
            positionY = page.getHeight() - top - height;
          }

          if (type === "text") {
            page.drawText(String(value || ""), {
              x: positionX,
              y: positionY + fontSize - 2, 
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          } else if (type === "signature") {
            // Extract signature image data from various possible formats
            let signatureImageData = null;
            let signatureCanvasSettings = null;
            
            if (value && value !== "[Signature]") {
              try {
                // Handle JSON string format
                if (typeof value === "string" && value.startsWith("{")) {
                  const signatureData = JSON.parse(value);
                  signatureImageData = signatureData.signature || signatureData.signatureData || signatureData.image || signatureData.data;
                } 
                // Handle direct base64 image data
                else if (typeof value === "string" && value.startsWith("data:image/")) {
                  signatureImageData = value;
                }
                // Handle object format
                else if (typeof value === "object" && value !== null) {
                  signatureImageData = value.signature || value.signatureData || value.image || value.data;
                }
                
                // Prefer signature sizing from canvas_settings when provided
                if (fieldData?.canvas_settings) {
                  try {
                    signatureCanvasSettings =
                      typeof fieldData.canvas_settings === "string"
                        ? JSON.parse(fieldData.canvas_settings)
                        : fieldData.canvas_settings;
                  } catch (parseError) {
                    console.error("Failed to parse canvas_settings JSON:", parseError);
                    signatureCanvasSettings = null;
                  }
                }

                const signatureDrawWidth =
                  (Number(signatureCanvasSettings?.width) > 0 ? signatureCanvasSettings.width : width || 40) * scaleX;
                const signatureDrawHeight =
                  (Number(signatureCanvasSettings?.height) > 0 ? signatureCanvasSettings.height : height || 40) * scaleY;

                // If we have valid image data, embed it
                if (signatureImageData && signatureImageData.startsWith("data:image/")) {
                  const pdfImage = await pdfDoc.embedPng(signatureImageData);
                  page.drawImage(pdfImage, {
                    x: positionX,
                    y: positionY,
                    width: signatureDrawWidth,
                    height: signatureDrawHeight,
                    zIndex: zIndex,
                  });
                }
              } catch (sigError) {
                console.error("Failed to process signature image:", sigError);
                // Continue without signature if it fails
              }
            }
          } else if (type === "initials") {
            page.drawText(String(value || "").toUpperCase(), {
              x: positionX,
              y: positionY + fontSize - 3, 
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          } else if (type === "checkbox") {
            const checkboxSize = 10;
            page.drawRectangle({
              x: positionX,
              y: positionY,
              width: checkboxSize,
              height: checkboxSize,
              borderColor: rgb(0, 0, 0),
              color: rgb(1, 1, 1),
              borderWidth: 1,
              zIndex: zIndex,
            });

            if (value) {
              try {
                const pdfImage = await pdfDoc.embedPng(checkMarkBase64);
                page.drawImage(pdfImage, {
                  x: positionX,
                  y: positionY,
                  width: checkboxSize,
                  height: checkboxSize,
                  zIndex: zIndex,
                });
              } catch (checkError) {
                // Continue without checkmark if it fails
              }
            }
          } else if (type === "textarea") {
            const textValue = value || "";
            const avgCharWidth = fontSize * 0.55; 
            const maxCharsPerLine = Math.floor((width - 20) / avgCharWidth); 

            const wrapText = (text) => {
              const lines = [];
              let currentLine = '';
              
              for (let i = 0; i < text.length; i++) {
                const char = text[i];
                const testLine = currentLine + char;
                
                if (testLine.length > maxCharsPerLine && currentLine) {
                  lines.push(currentLine);
                  currentLine = char;
                } else {
                  currentLine = testLine;
                }
              }
              
              if (currentLine) {
                lines.push(currentLine);
              }
              
              return lines;
            };
            
            const lines = textValue.split('\n');
            const allLines = [];
            
            lines.forEach(line => {
              if (line.trim()) {
                const wrappedLines = wrapText(line);
                allLines.push(...wrappedLines);
              }
            });

            allLines.forEach((line, index) => {
              page.drawText(line, {
                x: positionX,
                y: positionY - fontSize - index * fontSize, 
                size: fontSize,
                font,
                zIndex: zIndex,
              });
            });
          } else if (type === "select") {
            const selectValue = Array.isArray(value)
              ? value.join(", ")
              : value || "";
            page.drawText(String(selectValue), {
              x: positionX,
              y: positionY + fontSize,
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          } else if (type === "radio") {
            const radioSize = 7;
            page.drawEllipse({
              x: positionX,
              y: positionY,
              xScale: radioSize,
              yScale: radioSize,
              borderColor: rgb(0, 0, 0),
              color: rgb(1, 1, 1),
              borderWidth: 1,
              zIndex: zIndex,
            });

            const isThisFieldSelected = value === true && value !== null;
            
            if (isThisFieldSelected) {
              page.drawEllipse({
                x: positionX,
                y: positionY,
                xScale: radioSize - 2,
                yScale: radioSize - 2,
                color: rgb(0, 0, 0),
                zIndex: zIndex,
              });
            }
          } else if (type === "date") {
            let formattedDate = "";
            if (value) {
              try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const year = date.getFullYear();
                  formattedDate = `${month}-${day}-${year}`;
                } else {
                  formattedDate = String(value);
                }
              } catch (error) {
                formattedDate = String(value);
              }
            }
            page.drawText(formattedDate, {
              x: positionX,
              y: positionY + fontSize, 
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          }
        }
      }
      
      const modifiedPdfBytes = await pdfDoc.save();
      const modifiedPdfBlob = new Blob([modifiedPdfBytes], {
        type: "application/pdf",
      });
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(modifiedPdfBlob);
      downloadLink.download = "modified_pdf.pdf";

      downloadLink.click();
    } catch (error) {
      toast.error("Failed to generate PDF preview: " + error.message, {
        theme: "colored",
        autoClose: 3000,
      });
    }
  };

  const savePdf = async () => {
    try {
      const existingPdfBytes = await fetch(file).then((res) => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      
      for (const pageKey in settingsData.current) {
        const pageData = settingsData.current[pageKey];
        const pageIndex = parseInt(pageKey) - 1;
        
        if (pageIndex < 0 || pageIndex >= pdfDoc.getPageCount()) {
          console.warn(`Skipping page ${pageKey} - PDF only has ${pdfDoc.getPageCount()} pages`);
          continue;
        }
        
        const page = pdfDoc.getPage(pageIndex);
        
        // Calculate scale factor between canvas dimensions and actual PDF page dimensions
        const pdfPageWidth = page.getWidth();
        const pdfPageHeight = page.getHeight();
        const canvasWidth = (canvasData?.width && canvasData.width > 0) ? canvasData.width : pdfPageWidth;
        const canvasHeight = (canvasData?.height && canvasData.height > 0) ? canvasData.height : pdfPageHeight;
        const scaleX = canvasWidth > 0 ? pdfPageWidth / canvasWidth : 1;
        const scaleY = canvasHeight > 0 ? pdfPageHeight / canvasHeight : 1;
        
        for (const fieldKey in pageData) {
          const fieldData = pageData[fieldKey];
          const type = fieldData.type;
          const top = (fieldData.top || fieldData.y || 0) * scaleY;
          const left = (fieldData.left || fieldData.x || 0) * scaleX;
          const width = (fieldData.width || 100) * scaleX;
          const height = (fieldData.height || 30) * scaleY;
          const value = fieldData.value || "";
          const fontSize = parseFloat(fieldData.font_size || fieldData.fontSize) || 12;
          const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
          const zIndex = 99;

          let positionX, positionY;
          if (type === "checkbox" || type === "radio") {
            const elementSize = (type === "checkbox") ? 10 : 7; 
            positionX = left + (width / 2) - (elementSize / 2); 
            positionY = page.getHeight() - top - (height / 2) - (elementSize / 2); 
            if (type === "radio") {
              positionY += 3.75;
              positionX += 7.5; // Move radio fields 10px to the right
            }
          } else if (type === "textarea") {
            positionX = left + 3.75; 
            positionY = Math.max(0, page.getHeight() - top); 
          } else {
            positionX = left + 3.75; 
            positionY = page.getHeight() - top - height;
          }

          if (type === "text") {
            page.drawText(String(value || ""), {
              x: positionX,
              y: positionY + fontSize - 2, 
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          } else if (type === "signature") {
            // Extract signature image data from various possible formats
            let signatureImageData = null;
            let signatureCanvasSettings = null;
            
            if (value && value !== "[Signature]") {
              try {
                // Handle JSON string format
                if (typeof value === "string" && value.startsWith("{")) {
                  const signatureData = JSON.parse(value);
                  signatureImageData = signatureData.signature || signatureData.signatureData || signatureData.image || signatureData.data;
                } 
                // Handle direct base64 image data
                else if (typeof value === "string" && value.startsWith("data:image/")) {
                  signatureImageData = value;
                }
                // Handle object format
                else if (typeof value === "object" && value !== null) {
                  signatureImageData = value.signature || value.signatureData || value.image || value.data;
                }
                
                // Prefer signature sizing from canvas_settings when provided
                if (fieldData?.canvas_settings) {
                  try {
                    signatureCanvasSettings =
                      typeof fieldData.canvas_settings === "string"
                        ? JSON.parse(fieldData.canvas_settings)
                        : fieldData.canvas_settings;
                  } catch (parseError) {
                    console.error("Failed to parse canvas_settings JSON:", parseError);
                    signatureCanvasSettings = null;
                  }
                }

                const signatureDrawWidth =
                  (Number(signatureCanvasSettings?.width) > 0 ? signatureCanvasSettings.width : width || 40) * scaleX;
                const signatureDrawHeight =
                  (Number(signatureCanvasSettings?.height) > 0 ? signatureCanvasSettings.height : height || 40) * scaleY;

                // If we have valid image data, embed it
                if (signatureImageData && signatureImageData.startsWith("data:image/")) {
                  const pdfImage = await pdfDoc.embedPng(signatureImageData);
                  page.drawImage(pdfImage, {
                    x: positionX,
                    y: positionY,
                    width: signatureDrawWidth,
                    height: signatureDrawHeight,
                    zIndex: zIndex,
                  });
                }
              } catch (sigError) {
                console.error("Failed to process signature image:", sigError);
              }
            }
          } else if (type === "initials") {
            page.drawText(String(value || "").toUpperCase(), {
              x: positionX,
              y: positionY + fontSize - 3, 
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          } else if (type === "checkbox") {
            const checkboxSize = 10;

            page.drawRectangle({
              x: positionX,
              y: positionY,
              width: checkboxSize,
              height: checkboxSize,
              borderColor: rgb(0, 0, 0),
              color: rgb(1, 1, 1),
              borderWidth: 1,
              zIndex: zIndex,
            });

            if (value) {
              const pdfImage = await pdfDoc.embedPng(checkMarkBase64);

              page.drawImage(pdfImage, {
                x: positionX,
                y: positionY,
                width: checkboxSize,
                height: checkboxSize,
                zIndex: zIndex,
              });
            }
          } else if (type === "textarea") {
            const textValue = value || "";
            const avgCharWidth = fontSize * 0.55; 
            const maxCharsPerLine = Math.floor((width - 20) / avgCharWidth); 

            const wrapText = (text) => {
              const lines = [];
              let currentLine = '';
              
              for (let i = 0; i < text.length; i++) {
                const char = text[i];
                const testLine = currentLine + char;
                
                if (testLine.length > maxCharsPerLine && currentLine) {
                  lines.push(currentLine);
                  currentLine = char;
                } else {
                  currentLine = testLine;
                }
              }
              
              if (currentLine) {
                lines.push(currentLine);
              }
              
              return lines;
            };
            
            const lines = textValue.split('\n');
            const allLines = [];
            
            lines.forEach(line => {
              if (line.trim()) {
                const wrappedLines = wrapText(line);
                allLines.push(...wrappedLines);
              }
            });

            allLines.forEach((line, index) => {
              page.drawText(line, {
                x: positionX,
                y: positionY - fontSize - index * fontSize, 
                size: fontSize,
                font,
                zIndex: zIndex,
              });
            });
          } else if (type === "select") {
            const selectValue = Array.isArray(value)
              ? value.join(", ")
              : value || "";
            page.drawText(String(selectValue), {
              x: positionX,
              y: positionY + fontSize, 
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          } else if (type === "radio") {
            const radioSize = 7;

            page.drawEllipse({
              x: positionX,
              y: positionY,
              xScale: radioSize,
              yScale: radioSize,
              borderColor: rgb(0, 0, 0),
              color: rgb(1, 1, 1),
              borderWidth: 1,
              zIndex: zIndex,
            });

            const isThisFieldSelected = value === true && value !== null;
            
            if (isThisFieldSelected) {
              page.drawEllipse({
                x: positionX,
                y: positionY,
                xScale: radioSize - 2,
                yScale: radioSize - 2,
                color: rgb(0, 0, 0),
                zIndex: zIndex,
              });
            }
          } else if (type === "date") {
            
            let formattedDate = "";
            if (value) {
              try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const year = date.getFullYear();
                  formattedDate = `${month}-${day}-${year}`;
                } else {
                  formattedDate = String(value);
                }
              } catch (error) {
                formattedDate = String(value);
              }
            }
            page.drawText(formattedDate, {
              x: positionX,
              y: positionY + fontSize, 
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          }
        }
      }
      
      const modifiedPdfBytes = await pdfDoc.save();
      const modifiedPdfBlob = new Blob([modifiedPdfBytes], {
        type: "application/pdf",
      });
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(modifiedPdfBlob);
      downloadLink.download = "modified_pdf.pdf";

      submitPdfCall(downloadLink);
    } catch (error) {
      console.error("Error saving PDF:", error);
      toast.error("Failed to save PDF: " + error.message, {
        theme: "colored",
        autoClose: 3000,
      });
    }
  };

  const submitPdfCall = async (downloadLink) => {
    try {
      setLoading(true);
      
      const response = await fetch(downloadLink.href);
      const pdfBlob = await response.blob();
      
      const formData = new FormData();
      if (pageType === "profile_task") {
        formData.append("task_id", pdfFullData?.id);
      } else if (pageType === "employee-onboard") {
        formData.append("task_id", pdfFullData?.task_id);
      }
      formData.append("pdf_file", pdfBlob);
      formData.append("page_type", pageType);
      formData.append("settings", JSON.stringify(settingsData?.current));
      formData.append(
        "canvas_settings",
        pdfFullData?.custom_documents?.canvas_settings,
      );
      formData.append("document_name", pdfFullData?.name);

      const res = await postUploadData("custom-docs-completed", {}, formData);
      if (res.status === true) {
        setLoading(false);
        if (pageType === "employee-onboard") {
          navigate("/employee-onboard-sections", {
            state: pdfFullData,
          });
        } else if (pageType === "profile_task") {
          const state = {
            state: "tasks_custom_pdf",
            user_id: pdfFullData.user_id,
          };
          navigate("/profile/" + pdfFullData.user_id, { state: state });
        }
        toast.success(res.message, {
          theme: "colored",
          autoClose: 1000,
        });
      } else {
        toast.error(res.message || "Failed to submit PDF", {
          theme: "colored",
          autoClose: 1000,
        });
        setLoading(false);
      }
    } catch (error) {
      console.error("Error submitting PDF:", error);
      toast.error("Failed to submit PDF: " + error.message, {
        theme: "colored",
        autoClose: 3000,
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const headerRect = headerRef.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;

        // Check if scrollTop touches the header (header is at the top of viewport)
        if (headerRect.top <= 0 && scrollTop > 0) {
          setIsHeaderTouched(true);
        } else {
          setIsHeaderTouched(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      {!loading && (
        <>
          {/* Header Section */}
          <div className={`custom-document-header employee-onboard-pdf-header ${isHeaderTouched ? "header-sticky" : ""}`} ref={headerRef}>
            <div className="left-column">
              <BackButtonComponent
                hasHistoryback
                onClick={handleCancelBtnClick}
              />
              <div className="file-name-edit-flex-wrapper">
                <p className="file-name-edit-text">{pdfFullData?.name}</p>
              </div>
            </div>
            <div className="right-column">
              <div className="clear-button-wrapper">
                <button className="clear-button" onClick={handleCancelBtnClick}>
                  <img src={CancelIcon} alt="cancel-icon" />
                  <p className="button-text">CANCEL</p>
                </button>
              </div>
              <div className="preview-button-wrapper">
                <button className="preview-button" onClick={DownloadPdf}>
                  <img src={Preview} alt="preview-icon" />
                  <p className="button-text">PREVIEW</p>
                </button>
              </div>
              <div className="save-submit-button-wrapper">
                <button className="save-button" onClick={handleSaveBtnClick}>
                  <img src={SaveIcon} alt="save-icon" />
                  <p className="button-text">SUBMIT</p>
                </button>
              </div>
            </div>
          </div>

          <div className="custom-client-document-content">
            {/* Left Sidebar */}
            <div className="left-column-wrapper">
              <div className="sticky-menu">
                <div className="page-number">
                  <p>Page</p>
                  {numPages <= 1 ? (
                    <div className="pdf-number">
                      <span className="start">{selectPage}</span>
                    </div>
                  ) : (
                    <>
                      <p
                        className={`arrow arrow-up menu-item${selectPage <= 1 ? " disabled" : ""}`}
                        onClick={handlePreviousPage}
                      >
                        <img src={Arrow} alt="arrow-up" />
                        <span className="menu-label">Previous Page</span>
                      </p>
                      <div className="pdf-number">
                        <span className="start">{selectPage}</span>
                        <span className="end">/{numPages}</span>
                      </div>
                      <p
                        className={`arrow arrow-down menu-item${selectPage >= numPages ? " disabled" : ""}`}
                        onClick={handleNextPage}
                      >
                        <img src={Arrow} alt="arrow-down" />
                        <span className="menu-label">Next Page</span>
                      </p>
                    </>
                  )}
                </div>
                <ZoomControls
                  zoom={zoom}
                  onZoomIn={handleZoomIn}
                  onZoomOut={handleZoomOut}
                />
              </div>
            </div>

            {/* Main Content Area */}
            <div className="pdf-content-area">
              <div
                className="react-custom-pdf"
                // style={{
                //   overflowX: pageType === 'employee-onboard' ? 'hidden' : ''
                // }}
              >
                <div id="html-to-pdf" style={containerStyle}>
                  <div>
                    {file ? (
                      <Document
                        file={file}
                        onLoadSuccess={onPdfLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        loading={<Loader />}
                        error={null}
                        noData={null}
                      >
                        <Page
                          pageNumber={selectPage}
                          scale={zoom}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          className="pdf-page"
                        />
                      </Document>
                    ) : (
                      <Loader />
                    )}
                    {/* Render inputs based on current page */}
                    {!isPdfLoading && (
                      <FieldsRenderer
                        selectPage={selectPage}
                        activeFieldId={activeFieldId}
                        handleChange={handleChange}
                        handleFieldOpen={handleFieldOpen}
                        handleFieldClose={handleFieldClose}
                        enableErrorMsg={enableErrorMsg}
                        personalInfo={personalInfo}
                        onboardingTasks={onboardingTasks}
                        pdfFullData={pdfFullData}
                        settingsData={settingsData}
                        validationErrors={validationErrors}
                        zoom={zoom}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {loading && <Loader />}
    </>
  );
};

export default PdfViewer;
